<script>
    import { onMount } from 'svelte';
    import { channelsAPI, chatbotAPI } from './api.js';

    export let channel;
    export let onClose;

    let loading = false;
    let error = null;
    let success = null;
    let activeTab = 'settings';
    let integration = null;
    let chatbots = [];
    let webhookEvents = [];

    // Channel settings
    let channelData = { ...channel };

    // Integration form data
    let integrationData = {
        external_account_id: '',
        external_account_name: '',
        credentials: {},
        webhook_verify_token: '',
        api_version: 'v21.0',
        api_endpoint: '',
        is_active: true
    };

    onMount(() => {
        loadData();
    });

    async function loadData() {
        loading = true;
        try {
            const [chatbotsData, integrationData] = await Promise.all([
                chatbotAPI.getAll(),
                loadIntegration()
            ]);
            chatbots = chatbotsData;
            // For an already-configured WhatsApp QR channel, show its real
            // connection state instead of the default 'disconnected'.
            if (isWhatsAppQR()) {
                refreshWaStatus();
            }
        } catch (err) {
            console.error('Error loading data:', err);
        } finally {
            loading = false;
        }
    }

    async function loadIntegration() {
        try {
            const resp = await channelsAPI.getIntegration(channel.id);
            // Backend now wraps the integration to distinguish "no integration yet"
            // (resp.integration === null) from a real error.
            integration = resp?.integration || null;
            if (integration) {
                integrationData = {
                    external_account_id: integration.external_account_id || '',
                    external_account_name: integration.external_account_name || '',
                    credentials: integration.credentials || {},
                    webhook_verify_token: '',  // Don't show for security
                    api_version: integration.api_version || 'v21.0',
                    api_endpoint: integration.api_endpoint || '',
                    is_active: integration.is_active
                };
            }
        } catch (err) {
            console.warn('loadIntegration failed (channel may not have one yet):', err.message);
            integration = null;
        }
    }

    async function updateChannel() {
        loading = true;
        error = null;
        success = null;

        try {
            await channelsAPI.update(channel.id, channelData);
            success = 'Canal actualizado correctamente';
            setTimeout(() => success = null, 3000);
        } catch (err) {
            error = err.message || 'Error al actualizar canal';
        } finally {
            loading = false;
        }
    }

    async function saveIntegration() {
        loading = true;
        error = null;
        success = null;

        try {
            await channelsAPI.createIntegration(channel.id, integrationData);
            success = 'Integración guardada correctamente';
            await loadIntegration();
            setTimeout(() => success = null, 3000);
        } catch (err) {
            error = err.message || 'Error al guardar integración';
        } finally {
            loading = false;
        }
    }

    async function testIntegration() {
        loading = true;
        error = null;
        success = null;

        try {
            const result = await channelsAPI.testIntegration(channel.id);
            success = result.message || 'Prueba exitosa';
            setTimeout(() => success = null, 5000);
        } catch (err) {
            error = err.message || 'Error en la prueba de integración';
        } finally {
            loading = false;
        }
    }

    async function loadWebhookEvents() {
        loading = true;
        try {
            const result = await channelsAPI.getWebhookEvents(channel.id);
            webhookEvents = result.events || [];
        } catch (err) {
            error = err.message || 'Error al cargar eventos webhook';
        } finally {
            loading = false;
        }
    }

    // WhatsApp QR specific state.
    // Evolution API (nginx /wa-api-evo -> :3010) is the default backend. A channel
    // only uses the legacy service (/wa-api -> :3001) if flagged use_legacy.
    $: WA_BASE = (integration?.channel_metadata?.use_legacy) ? '/wa-api' : '/wa-api-evo';
    let qrCode = null;
    let waStatus = 'disconnected';
    let waUser = null;
    let qrPollingInterval = null;

    /**
     * Resilient fetch for the WA microservice. Detects HTML responses (e.g.
     * the service is down and nginx returns its 502 page) and surfaces a
     * clean error instead of "Unexpected token '<' is not valid JSON".
     */
    async function waFetch(path, opts = {}) {
        let response;
        try {
            response = await fetch(`${WA_BASE}${path}`, opts);
        } catch (e) {
            throw new Error('No se pudo contactar el servicio de WhatsApp QR.');
        }
        const ct = response.headers.get('content-type') || '';
        if (!ct.toLowerCase().includes('application/json')) {
            const txt = await response.text().catch(() => '');
            const snippet = txt.replace(/\s+/g, ' ').trim().slice(0, 80);
            if (response.status === 404 || response.status === 502) {
                throw new Error('El servicio de WhatsApp QR no está disponible. Verifica que el contenedor "whatsapp" esté corriendo.');
            }
            throw new Error(`Respuesta inesperada del servicio (HTTP ${response.status}): ${snippet || 'sin cuerpo'}`);
        }
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(body.error || `HTTP ${response.status}`);
        }
        return body;
    }

    async function connectWhatsAppQR() {
        loading = true;
        error = null;
        try {
            const result = await waFetch(`/sessions/${channel.id}/connect`, { method: 'POST' });
            if (result.status === 'connected') {
                waStatus = 'connected';
                waUser = result.user;
                success = 'WhatsApp ya está conectado';
            } else {
                startQRPolling();
                success = 'Iniciando conexión... escanea el código QR cuando aparezca.';
            }
        } catch (err) {
            error = 'Error al conectar con servicio WhatsApp QR: ' + err.message;
        } finally {
            loading = false;
        }
    }

    function startQRPolling() {
        if (qrPollingInterval) clearInterval(qrPollingInterval);
        let consecutiveErrors = 0;
        qrPollingInterval = setInterval(async () => {
            try {
                const status = await waFetch(`/sessions/${channel.id}/status`);
                consecutiveErrors = 0;
                waStatus = status.status;
                waUser = status.user;

                if (status.hasQR) {
                    try {
                        const qrData = await waFetch(`/sessions/${channel.id}/qr`);
                        qrCode = qrData.qr;
                    } catch {}
                }

                if (status.status === 'connected') {
                    clearInterval(qrPollingInterval);
                    qrPollingInterval = null;
                    qrCode = null;
                    success = '¡WhatsApp conectado exitosamente!';
                }
            } catch (err) {
                consecutiveErrors += 1;
                console.warn('QR polling error:', err.message);
                if (consecutiveErrors >= 5) {
                    clearInterval(qrPollingInterval);
                    qrPollingInterval = null;
                    error = 'Perdimos contacto con el servicio WhatsApp. ' + err.message;
                }
            }
        }, 2000);
    }

    // Reflect the REAL session state when opening an already-configured channel.
    // Without this the panel always starts as 'disconnected' until the user runs
    // the QR flow, even though WhatsApp is connected and chat works fine.
    async function refreshWaStatus() {
        try {
            const status = await waFetch(`/sessions/${channel.id}/status`);
            waStatus = status.status || 'disconnected';
            waUser = status.user || null;
            if (status.hasQR) {
                try {
                    const qrData = await waFetch(`/sessions/${channel.id}/qr`);
                    qrCode = qrData.qr;
                } catch {}
            }
        } catch (err) {
            // Service unreachable: leave the default 'disconnected' but don't spam errors.
            console.warn('refreshWaStatus:', err.message);
        }
    }

    async function disconnectWhatsAppQR() {
        loading = true;
        try {
            await waFetch(`/sessions/${channel.id}/logout`, { method: 'POST' });
            waStatus = 'disconnected';
            waUser = null;
            qrCode = null;
            if (qrPollingInterval) {
                clearInterval(qrPollingInterval);
                qrPollingInterval = null;
            }
            success = 'Sesión cerrada correctamente';
        } catch (err) {
            error = 'Error al desconectar: ' + err.message;
        } finally {
            loading = false;
        }
    }

    function getIntegrationFields() {
        switch (channel.channel_type) {
            case 'WHATSAPP':
            case 'whatsapp':
                return [
                    { key: 'access_token', label: 'Access Token', type: 'password', required: true },
                    { key: 'phone_number_id', label: 'Phone Number ID', type: 'text', required: true },
                    { key: 'business_account_id', label: 'Business Account ID', type: 'text', required: true }
                ];
            case 'WHATSAPP_QR':
            case 'whatsapp_qr':
                return []; // No credentials needed, uses QR code
            case 'FACEBOOK':
            case 'facebook':
            case 'INSTAGRAM':
            case 'instagram':
                return [
                    { key: 'page_access_token', label: 'Page Access Token', type: 'password', required: true },
                    { key: 'page_id', label: 'Page ID / Instagram Account ID', type: 'text', required: true }
                ];
            case 'EMAIL':
            case 'email':
                return [
                    { key: 'smtp_server', label: 'SMTP Server', type: 'text', required: true },
                    { key: 'smtp_port', label: 'SMTP Port', type: 'number', required: true },
                    { key: 'smtp_user', label: 'SMTP User', type: 'text', required: true },
                    { key: 'smtp_password', label: 'SMTP Password', type: 'password', required: true }
                ];
            case 'TELEGRAM':
            case 'telegram':
                return [
                    { key: 'bot_token', label: 'Bot Token', type: 'password', required: true }
                ];
            default:
                return [];
        }
    }

    function isWhatsAppQR() {
        return channel.channel_type === 'WHATSAPP_QR' || channel.channel_type === 'whatsapp_qr';
    }

    function setTab(tab) {
        activeTab = tab;
        if (tab === 'webhooks') {
            loadWebhookEvents();
        }
    }

    function generateWebhookUrl() {
        const baseUrl = window.location.origin;
        const webhookPath = {
            'whatsapp': '/api/webhooks/whatsapp',
            'WHATSAPP': '/api/webhooks/whatsapp',
            'whatsapp_qr': '/api/webhooks/whatsapp-qr',
            'WHATSAPP_QR': '/api/webhooks/whatsapp-qr',
            'facebook': '/api/webhooks/meta',
            'FACEBOOK': '/api/webhooks/meta',
            'instagram': '/api/webhooks/meta',
            'INSTAGRAM': '/api/webhooks/meta',
            'web_widget': '/api/webhooks/widget',
            'WEB_WIDGET': '/api/webhooks/widget'
        };
        return baseUrl + (webhookPath[channel.channel_type] || '/api/webhooks/generic');
    }

    function copyToClipboard(text) {
        navigator.clipboard.writeText(text);
        success = 'Copiado al portapapeles';
        setTimeout(() => success = null, 2000);
    }
</script>

<style>
    .config-container {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: white;
        z-index: 100;
        display: flex;
        flex-direction: column;
    }

    .config-header {
        background: #1a1d2e;
        color: white;
        padding: 20px 30px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #2a2d3e;
    }

    .header-left {
        display: flex;
        align-items: center;
        gap: 15px;
    }

    .back-button {
        background: rgba(255, 255, 255, 0.1);
        border: none;
        color: white;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: background 0.2s;
    }

    .back-button:hover {
        background: rgba(255, 255, 255, 0.2);
    }

    .header-title h2 {
        margin: 0;
        font-size: 20px;
    }

    .header-subtitle {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.7);
        margin-top: 4px;
    }

    .config-body {
        flex: 1;
        display: flex;
        overflow: hidden;
    }

    .tabs-nav {
        width: 220px;
        background: #f5f7fb;
        border-right: 1px solid #e0e0e0;
        padding: 20px 0;
    }

    .tab-button {
        width: 100%;
        padding: 12px 20px;
        border: none;
        background: none;
        text-align: left;
        cursor: pointer;
        color: #666;
        font-size: 14px;
        transition: all 0.2s;
        border-left: 3px solid transparent;
    }

    .tab-button:hover {
        background: rgba(33, 150, 243, 0.05);
        color: #2196f3;
    }

    .tab-button.active {
        background: white;
        color: #2196f3;
        font-weight: 600;
        border-left-color: #2196f3;
    }

    .tab-content {
        flex: 1;
        padding: 30px;
        overflow-y: auto;
    }

    .section {
        max-width: 800px;
        margin-bottom: 30px;
    }

    .section h3 {
        margin: 0 0 15px 0;
        color: #333;
        font-size: 18px;
    }

    .form-group {
        margin-bottom: 20px;
    }

    .form-group label {
        display: block;
        margin-bottom: 8px;
        color: #555;
        font-weight: 500;
        font-size: 14px;
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        font-family: inherit;
    }

    .form-group textarea {
        min-height: 100px;
        resize: vertical;
    }

    .form-group small {
        display: block;
        margin-top: 5px;
        color: #757575;
        font-size: 12px;
    }

    .checkbox-group {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
    }

    .checkbox-group input[type="checkbox"] {
        width: auto;
        cursor: pointer;
    }

    .btn {
        padding: 10px 20px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        transition: all 0.2s;
    }

    .btn-primary {
        background: #2196f3;
        color: white;
    }

    .btn-primary:hover {
        background: #1976d2;
    }

    .btn-success {
        background: #4caf50;
        color: white;
    }

    .btn-success:hover {
        background: #388e3c;
    }

    .btn-secondary {
        background: #757575;
        color: white;
    }

    .btn-secondary:hover {
        background: #616161;
    }

    .actions {
        display: flex;
        gap: 10px;
        margin-top: 20px;
    }

    .alert {
        padding: 12px 16px;
        border-radius: 4px;
        margin-bottom: 20px;
    }

    .alert-success {
        background: #e8f5e9;
        color: #2e7d32;
        border-left: 4px solid #4caf50;
    }

    .alert-error {
        background: #ffebee;
        color: #c62828;
        border-left: 4px solid #f44336;
    }

    .webhook-url-box {
        background: #f5f5f5;
        padding: 15px;
        border-radius: 4px;
        margin-bottom: 20px;
    }

    .webhook-url-box label {
        display: block;
        font-weight: 600;
        margin-bottom: 8px;
        color: #333;
        font-size: 13px;
    }

    .url-display {
        display: flex;
        gap: 10px;
        align-items: center;
    }

    .url-display input {
        flex: 1;
        padding: 10px;
        background: white;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-family: monospace;
        font-size: 13px;
    }

    .copy-button {
        background: #2196f3;
        color: white;
        border: none;
        padding: 10px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 13px;
        white-space: nowrap;
    }

    .copy-button:hover {
        background: #1976d2;
    }

    .integration-status {
        padding: 15px;
        background: #f5f5f5;
        border-radius: 4px;
        margin-bottom: 20px;
    }

    .status-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 8px;
        font-size: 14px;
    }

    .status-label {
        color: #666;
    }

    .status-value {
        font-weight: 600;
        color: #333;
    }

    .status-value.active {
        color: #4caf50;
    }

    .status-value.inactive {
        color: #f44336;
    }

    .webhook-events-list {
        max-height: 500px;
        overflow-y: auto;
    }

    .event-item {
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        padding: 15px;
        margin-bottom: 10px;
    }

    .event-header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 8px;
    }

    .event-type {
        font-weight: 600;
        color: #333;
    }

    .event-time {
        font-size: 12px;
        color: #757575;
    }

    .event-status {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 3px;
        font-size: 11px;
        font-weight: 600;
    }

    .event-status.processed {
        background: #e8f5e9;
        color: #2e7d32;
    }

    .event-status.pending {
        background: #fff3e0;
        color: #e65100;
    }

    /* WhatsApp QR Styles */
    .whatsapp-qr-section {
        background: #f5f5f5;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 20px;
    }

    .qr-status-box {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 15px;
        background: white;
        border-radius: 6px;
        margin-bottom: 20px;
    }

    .status-indicator {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #9e9e9e;
    }

    .status-indicator.connected {
        background: #4caf50;
    }

    .status-indicator.connecting {
        background: #ff9800;
        animation: pulse 1.5s infinite;
    }

    @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
    }

    .qr-code-container {
        text-align: center;
        padding: 20px;
        background: white;
        border-radius: 8px;
        margin-bottom: 20px;
    }

    .qr-code-container img {
        max-width: 256px;
        border: 4px solid #128c7e;
        border-radius: 8px;
    }

    .qr-code-container p {
        margin-top: 12px;
        color: #666;
        font-size: 14px;
    }

    .qr-actions {
        display: flex;
        gap: 10px;
        justify-content: center;
    }

    .btn-whatsapp {
        background: #128c7e;
        color: white;
    }

    .btn-whatsapp:hover {
        background: #0d7a6e;
    }

    .btn-danger {
        background: #f44336;
        color: white;
    }

    .btn-danger:hover {
        background: #d32f2f;
    }

    .qr-info-box {
        background: #e3f2fd;
        border-left: 4px solid #2196f3;
        padding: 12px 16px;
        border-radius: 4px;
        margin-top: 20px;
        font-size: 13px;
        color: #1565c0;
    }
</style>

<div class="config-container">
    <div class="config-header">
        <div class="header-left">
            <button class="back-button" on:click={onClose}>
                ← Volver
            </button>
            <div class="header-title">
                <h2>Configuración: {channel.name}</h2>
                <div class="header-subtitle">{channel.channel_type}</div>
            </div>
        </div>
    </div>

    <div class="config-body">
        <div class="tabs-nav">
            <button class="tab-button" class:active={activeTab === 'settings'} on:click={() => setTab('settings')}>
                ⚙️ Configuración General
            </button>
            <button class="tab-button" class:active={activeTab === 'integration'} on:click={() => setTab('integration')}>
                🔌 Integración
            </button>
            <button class="tab-button" class:active={activeTab === 'webhooks'} on:click={() => setTab('webhooks')}>
                📡 Webhooks & Logs
            </button>
        </div>

        <div class="tab-content">
            {#if success}
                <div class="alert alert-success">{success}</div>
            {/if}
            {#if error}
                <div class="alert alert-error">{error}</div>
            {/if}

            {#if activeTab === 'settings'}
                <div class="section">
                    <h3>Configuración del Canal</h3>

                    <div class="form-group">
                        <label for="name">Nombre del Canal</label>
                        <input id="name" type="text" bind:value={channelData.name} />
                    </div>

                    <div class="form-group">
                        <label for="description">Descripción</label>
                        <textarea id="description" bind:value={channelData.description}></textarea>
                    </div>

                    <div class="checkbox-group">
                        <input id="greeting_enabled" type="checkbox" bind:checked={channelData.greeting_enabled} />
                        <label for="greeting_enabled">Enviar saludo automático</label>
                    </div>

                    {#if channelData.greeting_enabled}
                        <div class="form-group">
                            <label for="greeting_message">Mensaje de Saludo</label>
                            <textarea id="greeting_message" bind:value={channelData.greeting_message}></textarea>
                            <small>Se enviará cuando un nuevo contacto inicie una conversación.</small>
                        </div>
                    {/if}

                    <div class="checkbox-group">
                        <input id="enable_auto_ai" type="checkbox" bind:checked={channelData.enable_auto_ai} />
                        <label for="enable_auto_ai">Activar IA automáticamente</label>
                    </div>

                    {#if channelData.enable_auto_ai}
                        <div class="form-group">
                            <label for="default_chatbot_id">Chatbot Predeterminado</label>
                            <select id="default_chatbot_id" bind:value={channelData.default_chatbot_id}>
                                <option value={null}>Ninguno</option>
                                {#each chatbots.filter(c => c.is_active) as chatbot}
                                    <option value={chatbot.id}>{chatbot.name}</option>
                                {/each}
                            </select>
                        </div>
                    {/if}

                    {#if channel.channel_type === 'web_widget'}
                        <div class="form-group">
                            <label for="widget_color">Color del Widget</label>
                            <input id="widget_color" type="color" bind:value={channelData.widget_color} />
                        </div>

                        <div class="form-group">
                            <label for="widget_position">Posición del Widget</label>
                            <select id="widget_position" bind:value={channelData.widget_position}>
                                <option value="right">Derecha</option>
                                <option value="left">Izquierda</option>
                            </select>
                        </div>

                        <div class="form-group">
                            <label for="widget_bubble_text">Texto del Botón</label>
                            <input id="widget_bubble_text" type="text" bind:value={channelData.widget_bubble_text} />
                        </div>
                    {/if}

                    <div class="actions">
                        <button class="btn btn-success" on:click={updateChannel} disabled={loading}>
                            {loading ? 'Guardando...' : 'Guardar Cambios'}
                        </button>
                    </div>
                </div>

            {:else if activeTab === 'integration'}
                <div class="section">
                    <h3>Configuración de Integración</h3>

                    {#if isWhatsAppQR()}
                        <!-- WhatsApp QR Connection UI -->
                        <div class="whatsapp-qr-section">
                            <div class="qr-status-box">
                                <div class="status-indicator" class:connected={waStatus === 'connected'} class:pending={waStatus === 'qr_ready' || waStatus === 'connecting'}>
                                    {#if waStatus === 'connected'}
                                        <span class="status-icon">✓</span>
                                        <span>Conectado</span>
                                    {:else if waStatus === 'qr_ready'}
                                        <span class="status-icon">📱</span>
                                        <span>Escanea el QR</span>
                                    {:else if waStatus === 'connecting'}
                                        <span class="status-icon">⏳</span>
                                        <span>Conectando...</span>
                                    {:else}
                                        <span class="status-icon">○</span>
                                        <span>Desconectado</span>
                                    {/if}
                                </div>
                                {#if waUser}
                                    <div class="wa-user-info">
                                        <strong>{waUser.name || waUser.id}</strong>
                                        <small>{waUser.id}</small>
                                    </div>
                                {/if}
                            </div>

                            {#if qrCode}
                                <div class="qr-code-container">
                                    <img src={qrCode} alt="WhatsApp QR Code" class="qr-code-image" />
                                    <p class="qr-instructions">Abre WhatsApp en tu telefono, ve a Dispositivos vinculados y escanea este codigo QR</p>
                                </div>
                            {/if}

                            <div class="qr-actions">
                                {#if waStatus !== 'connected'}
                                    <button class="btn btn-success" on:click={connectWhatsAppQR} disabled={loading}>
                                        {loading ? 'Conectando...' : '📱 Conectar WhatsApp'}
                                    </button>
                                {:else}
                                    <button class="btn btn-danger" on:click={disconnectWhatsAppQR} disabled={loading}>
                                        {loading ? 'Desconectando...' : '🚪 Cerrar Sesion'}
                                    </button>
                                {/if}
                            </div>

                            <div class="qr-info-box">
                                <h4>Informacion Importante</h4>
                                <ul>
                                    <li>Esta conexion usa WhatsApp Web, no la API oficial</li>
                                    <li>Tu telefono debe estar conectado a internet</li>
                                    <li>La sesion puede cerrarse si WhatsApp detecta actividad inusual</li>
                                    <li>No uses este numero para enviar spam</li>
                                </ul>
                            </div>
                        </div>
                    {:else if integration}
                        <div class="integration-status">
                            <div class="status-row">
                                <span class="status-label">Estado:</span>
                                <span class="status-value" class:active={integration.is_active} class:inactive={!integration.is_active}>
                                    {integration.is_active ? 'Activo' : 'Inactivo'}
                                </span>
                            </div>
                            {#if integration.last_sync_at}
                                <div class="status-row">
                                    <span class="status-label">Última sincronización:</span>
                                    <span class="status-value">{new Date(integration.last_sync_at).toLocaleString('es-CR', { timeZone: 'America/Costa_Rica' })}</span>
                                </div>
                            {/if}
                            {#if integration.error_count > 0}
                                <div class="status-row">
                                    <span class="status-label">Errores:</span>
                                    <span class="status-value inactive">{integration.error_count}</span>
                                </div>
                            {/if}
                        </div>
                    {/if}

                    <div class="webhook-url-box">
                        <label>URL del Webhook:</label>
                        <div class="url-display">
                            <input type="text" value={generateWebhookUrl()} readonly />
                            <button class="copy-button" on:click={() => copyToClipboard(generateWebhookUrl())}>
                                📋 Copiar
                            </button>
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="external_account_id">ID de Cuenta Externa</label>
                        <input id="external_account_id" type="text" bind:value={integrationData.external_account_id} />
                        <small>Business Account ID, Page ID, etc.</small>
                    </div>

                    <div class="form-group">
                        <label for="external_account_name">Nombre de Cuenta</label>
                        <input id="external_account_name" type="text" bind:value={integrationData.external_account_name} />
                    </div>

                    {#each getIntegrationFields() as field}
                        <div class="form-group">
                            <label for={field.key}>{field.label} {field.required ? '*' : ''}</label>
                            {#if field.type === 'password'}
                                <input
                                    id={field.key}
                                    type="password"
                                    bind:value={integrationData.credentials[field.key]}
                                    required={field.required}
                                />
                            {:else if field.type === 'number'}
                                <input
                                    id={field.key}
                                    type="number"
                                    bind:value={integrationData.credentials[field.key]}
                                    required={field.required}
                                />
                            {:else}
                                <input
                                    id={field.key}
                                    type="text"
                                    bind:value={integrationData.credentials[field.key]}
                                    required={field.required}
                                />
                            {/if}
                        </div>
                    {/each}

                    <div class="form-group">
                        <label for="webhook_verify_token">Webhook Verify Token</label>
                        <input id="webhook_verify_token" type="password" bind:value={integrationData.webhook_verify_token} />
                        <small>Token secreto para verificar webhooks entrantes.</small>
                    </div>

                    <div class="form-group">
                        <label for="api_version">Versión de API</label>
                        <input id="api_version" type="text" bind:value={integrationData.api_version} />
                    </div>

                    <div class="actions">
                        <button class="btn btn-success" on:click={saveIntegration} disabled={loading}>
                            {loading ? 'Guardando...' : 'Guardar Integración'}
                        </button>
                        {#if integration}
                            <button class="btn btn-primary" on:click={testIntegration} disabled={loading}>
                                {loading ? 'Probando...' : '🧪 Probar Integración'}
                            </button>
                        {/if}
                    </div>
                </div>

            {:else if activeTab === 'webhooks'}
                <div class="section">
                    <h3>Eventos Webhook</h3>

                    {#if loading}
                        <p>Cargando eventos...</p>
                    {:else if webhookEvents.length === 0}
                        <p>No hay eventos webhook registrados.</p>
                    {:else}
                        <div class="webhook-events-list">
                            {#each webhookEvents as event}
                                <div class="event-item">
                                    <div class="event-header">
                                        <span class="event-type">{event.event_type}</span>
                                        <span class="event-time">{new Date(event.created_at).toLocaleString('es-CR', { timeZone: 'America/Costa_Rica' })}</span>
                                    </div>
                                    <span class="event-status" class:processed={event.processed} class:pending={!event.processed}>
                                        {event.processed ? 'Procesado' : 'Pendiente'}
                                    </span>
                                    {#if event.error_message}
                                        <div style="color: #f44336; font-size: 12px; margin-top: 8px;">
                                            Error: {event.error_message}
                                        </div>
                                    {/if}
                                </div>
                            {/each}
                        </div>
                    {/if}
                </div>
            {/if}
        </div>
    </div>
</div>
